import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../../infrastructure/api/default";
import LendingOperationsApi from "../../../../../infrastructure/api/lendingOperations";
import { getApiOrigin } from "../../../../../infrastructure/utils/resolveUploadUrl";
import { ModalTransition } from "../../../../components/shared/Dialog/Config";
import {
  getEntityId,
  parseApiErrorMessage,
} from "../../../../components/request/case-tabs/mehrsanOperationsUtils";
import { toJalaliFullDate } from "../../../../middleware/middleware";
import { parseAllRequestsDetail } from "../all-requests/allRequestsPageUtils";
import {
  buildLoanStageSteps,
  buildStepSavePayload,
  countCompletedSteps,
  getLoanStageTotalCount,
  mapUiStatusToLoanApiStatus,
  resolveProcessOverallStatus,
} from "../../shared/projectWorkspace/loanMehrsanStage/loanMehrsanStageAdapter";
import LoanMehrsanProcessStepsTable from "../../shared/projectWorkspace/loanMehrsanStage/LoanMehrsanProcessStepsTable";
import LoanMehrsanStepEditDialog from "../../shared/projectWorkspace/loanMehrsanStage/LoanMehrsanStepEditDialog";
import WorkflowActionsCard from "../../shared/projectWorkspace/loanMehrsanStage/WorkflowActionsCard";
import WorkflowDocumentsCard from "../../shared/projectWorkspace/loanMehrsanStage/WorkflowDocumentsCard";
import IriaPortalDetailPanel, {
  pickIriaPortalFields,
} from "../../shared/lending/IriaPortalDetailPanel";
import BarqPortalOtpLoginModal, {
  isBarqLoginRequired,
} from "../../shared/lending/BarqPortalOtpLoginModal";
import BarqPortalStagesTimeline from "../../shared/lending/BarqPortalStagesTimeline";

const OPERATIONS_SOURCE = "SolarentOperator";
const IRIA_PARTNER_CODE = "IRIA_PAY";
const BARQ_PARTNER_CODE = "BARQ_PAY";
const DETAIL_API = "SLN/SolarentOperator/AllRequests";

const InfoField = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || "—"}
    </Typography>
  </Grid>
);

InfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const formatAmount = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return `${num.toLocaleString("fa-IR")} ریال`;
};

const LendingWorkQueueCaseDrawer = ({
  open,
  queueRow,
  partnerTitle = "لندتک",
  onClose,
  onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const requestInfoId = queueRow?.requestInfoId;
  const loanProcessId = queueRow?.loanProcessId;

  const [loading, setLoading] = useState(false);
  const [requestDetail, setRequestDetail] = useState(null);
  const [fileBaseUrl, setFileBaseUrl] = useState("");
  const [loanProcess, setLoanProcess] = useState(null);
  const [editState, setEditState] = useState({ open: false, step: null });
  const [saving, setSaving] = useState(false);
  const [iriaDetail, setIriaDetail] = useState(null);
  const [iriaDetailLoading, setIriaDetailLoading] = useState(false);
  const [iriaSubmitting, setIriaSubmitting] = useState(false);
  const [barqDetail, setBarqDetail] = useState(null);
  const [barqDetailLoading, setBarqDetailLoading] = useState(false);
  const [barqLoginOpen, setBarqLoginOpen] = useState(false);
  const barqAutoRefreshKeyRef = useRef("");

  const isIriaCase =
    queueRow?.lendingPartnerCode === IRIA_PARTNER_CODE ||
    loanProcess?.lendingPartnerCode === IRIA_PARTNER_CODE ||
    loanProcess?.LendingPartnerCode === IRIA_PARTNER_CODE;

  const isBarqCase =
    queueRow?.lendingPartnerCode === BARQ_PARTNER_CODE ||
    loanProcess?.lendingPartnerCode === BARQ_PARTNER_CODE ||
    loanProcess?.LendingPartnerCode === BARQ_PARTNER_CODE;

  const loadData = useCallback(async () => {
    if (!requestInfoId || !loanProcessId) return;
    setLoading(true);
    try {
      const [detailRes, processRes] = await Promise.all([
        DefaultApiService.getOneApi(DETAIL_API, requestInfoId).catch(() => null),
        LendingOperationsApi.getById(OPERATIONS_SOURCE, loanProcessId),
      ]);

      if (detailRes?.isSuccess !== false) {
        const parsed = parseAllRequestsDetail(detailRes);
        setRequestDetail(parsed.request);
        setFileBaseUrl(parsed.baseUrl || getApiOrigin());
      } else {
        setRequestDetail(null);
        setFileBaseUrl(getApiOrigin());
      }

      const process =
        processRes?.isSuccess === false
          ? null
          : processRes?.data ?? processRes?.Data ?? null;
      setLoanProcess(process);
    } catch {
      setRequestDetail(null);
      setLoanProcess(null);
      setFileBaseUrl(getApiOrigin());
      toast.error("بارگذاری جزئیات فرآیند وام ناموفق بود.");
    } finally {
      setLoading(false);
    }
  }, [requestInfoId, loanProcessId]);

  useEffect(() => {
    if (!open || !requestInfoId) return;
    setEditState({ open: false, step: null });
    setIriaDetail(null);
    setBarqDetail(null);
    barqAutoRefreshKeyRef.current = "";
    loadData();
  }, [open, requestInfoId, loadData]);

  const handleRefreshIriaDetail = async () => {
    if (!loanProcessId || !isIriaCase) return;
    setIriaDetailLoading(true);
    try {
      const res = await LendingOperationsApi.refreshIriaPortalDetail(
        OPERATIONS_SOURCE,
        loanProcessId
      );
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "بروزرسانی جزئیات ایریاپی ناموفق بود."));
      }
      const data = res?.data ?? res?.Data ?? null;
      setIriaDetail(data);
      await loadData();
      onSaved?.();
      toast.success("جزئیات ایریاپی بروزرسانی شد.");
    } catch (err) {
      toast.error(err?.message || "بروزرسانی جزئیات ایریاپی ناموفق بود.");
      await loadData();
    } finally {
      setIriaDetailLoading(false);
    }
  };

  const handleSubmitIriaApplication = async () => {
    if (!loanProcessId || !isIriaCase) return;
    setIriaSubmitting(true);
    try {
      const res = await LendingOperationsApi.submitIriaPortalApplication(
        OPERATIONS_SOURCE,
        loanProcessId
      );
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "ارسال به ایریاپی ناموفق بود."));
      }
      const data = res?.data ?? res?.Data ?? {};
      const message =
        data?.connectorMessage ||
        data?.ConnectorMessage ||
        res?.message ||
        "متقاضی در ایریاپی ثبت شد.";
      toast.success(message);
      setIriaDetail(null);
      await loadData();
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "ارسال به ایریاپی ناموفق بود.");
    } finally {
      setIriaSubmitting(false);
    }
  };

  const handleRefreshBarqDetail = useCallback(
    async ({ silent = false } = {}) => {
      if (!loanProcessId || !isBarqCase) return;
      setBarqDetailLoading(true);
      try {
        const res = await LendingOperationsApi.refreshBarqPortalDetail(
          OPERATIONS_SOURCE,
          loanProcessId
        );
        if (isBarqLoginRequired(res)) {
          if (!silent) {
            setBarqLoginOpen(true);
            toast.info("برای بروزرسانی جزئیات، وارد پنل برق‌پی شوید");
          }
          return;
        }
        if (res?.isSuccess === false) {
          throw new Error(
            parseApiErrorMessage(res, "بروزرسانی جزئیات برق‌پی ناموفق بود.")
          );
        }
        const data = res?.data ?? res?.Data ?? null;
        setBarqDetail(data);
        await loadData();
        onSaved?.();
        if (!silent) toast.success("جزئیات برق‌پی بروزرسانی شد.");
      } catch (err) {
        if (!silent) {
          toast.error(err?.message || "بروزرسانی جزئیات برق‌پی ناموفق بود.");
        }
      } finally {
        setBarqDetailLoading(false);
      }
    },
    [loanProcessId, isBarqCase, loadData, onSaved]
  );

  // پس از باز شدن جزئیات، یک‌بار وضعیت/مراحل پنل برق‌پی را بگیر تا کنار مراحل سولارنت دیده شود.
  useEffect(() => {
    if (!open || loading || !isBarqCase || !loanProcessId) return;
    const key = `${loanProcessId}`;
    if (barqAutoRefreshKeyRef.current === key) return;
    barqAutoRefreshKeyRef.current = key;
    handleRefreshBarqDetail({ silent: true });
  }, [open, loading, isBarqCase, loanProcessId, handleRefreshBarqDetail]);

  const loanSteps = useMemo(
    () => (loanProcess ? buildLoanStageSteps([loanProcess]) : []),
    [loanProcess]
  );
  const loanCompleted = countCompletedSteps(loanSteps);
  const loanTotal = getLoanStageTotalCount(loanSteps);
  const loanOverall = resolveProcessOverallStatus(loanCompleted, loanTotal);

  const handleSaveStep = async (form) => {
    if (!editState.step || !loanProcessId) return;
    setSaving(true);
    try {
      const payload = buildStepSavePayload({
        domain: "loan",
        stepId: editState.step.id,
        ...form,
      });
      const res = await LendingOperationsApi.addLog(OPERATIONS_SOURCE, {
        LoanProcessId: loanProcessId,
        Status: mapUiStatusToLoanApiStatus(form.status),
        TrackingCode: form.referenceCode || null,
        ...payload,
      });
      if (res?.isSuccess === false) {
        throw new Error(parseApiErrorMessage(res, "ذخیره مرحله وام ناموفق بود."));
      }

      toast.success("مرحله وام ذخیره شد.");
      setEditState({ open: false, step: null });
      await loadData();
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "ذخیره مرحله ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  const detail = requestDetail || {};
  const titleName =
    queueRow?.applicantFullName ||
    detail.fullName ||
    `${detail.firstName || ""} ${detail.lastName || ""}`.trim() ||
    "—";
  const trackingCode =
    queueRow?.requestTrackingCode ||
    detail.referenceID ||
    detail.ReferenceID ||
    requestInfoId;
  const capacityKw =
    queueRow?.plantCapacityKw ??
    detail.plantCapacity ??
    detail.PlantCapacity ??
    null;
  const province =
    detail.provinceTitle ||
    detail.ProvinceTitle ||
    detail.provinceName ||
    detail.ProvinceName ||
    "";
  const city =
    detail.cityTitle || detail.CityTitle || detail.cityName || detail.CityName || "";
  const requestedAmount = formatAmount(queueRow?.requestedAmount);
  const approvedAmount = formatAmount(queueRow?.approvedAmount);
  const iriaPortalFields = pickIriaPortalFields(queueRow, loanProcess, iriaDetail);
  const barqPortalFields = pickIriaPortalFields(queueRow, loanProcess, barqDetail);
  const needsIriaSubmit = useMemo(() => {
    if (!isIriaCase) return false;
    const code = String(
      iriaPortalFields.externalApplicationId ||
        queueRow?.trackingCode ||
        loanProcess?.trackingCode ||
        loanProcess?.TrackingCode ||
        ""
    ).trim();
    return !/^\d+$/.test(code);
  }, [isIriaCase, iriaPortalFields.externalApplicationId, queueRow, loanProcess]);
  const barqStages =
    barqDetail?.stages || barqDetail?.Stages || [];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={ModalTransition}
        fullWidth
        fullScreen={isMobile}
        maxWidth="lg"
        scroll="paper"
        sx={{ zIndex: theme.zIndex.modal }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            minHeight: isMobile ? "100dvh" : "min(560px, 85vh)",
            maxHeight: isMobile ? "100dvh" : "92vh",
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={800} noWrap fontSize="1.05rem">
                پیگیری {partnerTitle} — {titleName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                کد پیگیری {trackingCode || "—"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton
                aria-label="بروزرسانی"
                onClick={loadData}
                size="small"
                disabled={loading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton aria-label="بستن" onClick={onClose} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        <DialogContent
          dividers
          sx={{
            pt: 1.5,
            pb: 2,
            flex: 1,
            minHeight: isMobile ? 280 : 360,
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : !queueRow ? (
            <Alert severity="warning">اطلاعات فرآیند وام یافت نشد.</Alert>
          ) : (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  اطلاعات پروژه و وام
                </Typography>
                <Grid container spacing={2}>
                  <InfoField label="کد پیگیری" value={trackingCode} />
                  <InfoField label="متقاضی" value={titleName} />
                  <InfoField
                    label="موبایل"
                    value={
                      queueRow.applicantMobile ||
                      detail.mobile ||
                      detail.Mobile ||
                      ""
                    }
                  />
                  <InfoField
                    label="ظرفیت"
                    value={capacityKw ? `${capacityKw} kW` : "—"}
                  />
                  <InfoField label="ارائه‌دهنده" value={partnerTitle} />
                  <InfoField
                    label="مسئول پیگیری"
                    value={queueRow.assignedToFullName || "بدون مسئول"}
                  />
                  <InfoField
                    label="وضعیت فرآیند"
                    value={queueRow.currentStatusTitle || "—"}
                  />
                  <InfoField
                    label="مرحله فعلی"
                    value={
                      queueRow.queueTab === "completed"
                        ? "تکمیل شده"
                        : queueRow.currentStageTitle || "—"
                    }
                  />
                  <InfoField
                    label="پیشرفت مراحل"
                    value={`${queueRow.completedStepCount ?? loanCompleted}/${queueRow.totalStepCount ?? loanTotal}`}
                  />
                  <InfoField label="کد رهگیری وام" value={queueRow.trackingCode} />
                  <InfoField label="شماره مرجع" value={queueRow.referenceNumber} />
                  {requestedAmount ? (
                    <InfoField label="مبلغ درخواستی" value={requestedAmount} />
                  ) : null}
                  {approvedAmount ? (
                    <InfoField label="مبلغ مصوب" value={approvedAmount} />
                  ) : null}
                  <InfoField
                    label="آخرین اقدام"
                    value={
                      queueRow.lastActionAt
                        ? toJalaliFullDate(queueRow.lastActionAt)
                        : "—"
                    }
                  />
                  {queueRow.queueTab !== "completed" ? (
                    <InfoField
                      label="روزهای باز"
                      value={`${queueRow.openDays ?? 0} روز`}
                    />
                  ) : null}
                  {province || city ? (
                    <InfoField
                      label="موقعیت"
                      value={[province, city].filter(Boolean).join(" / ")}
                    />
                  ) : null}
                </Grid>
                <Box sx={{ mt: 1.5 }}>
                  <Chip
                    size="small"
                    color={
                      loanOverall.key === "completed"
                        ? "success"
                        : loanOverall.key === "inProgress"
                        ? "info"
                        : "default"
                    }
                    label={`وضعیت کلی وام: ${loanOverall.label}`}
                  />
                </Box>
              </Paper>

              {isIriaCase ? (
                <IriaPortalDetailPanel
                  {...iriaPortalFields}
                  loading={iriaDetailLoading || iriaSubmitting}
                  onRefresh={handleRefreshIriaDetail}
                  showSubmit={needsIriaSubmit}
                  onSubmit={handleSubmitIriaApplication}
                  submitLabel="ارسال به ایریاپی"
                />
              ) : null}

              {isBarqCase ? (
                <>
                  <IriaPortalDetailPanel
                    {...barqPortalFields}
                    title="وضعیت در پنل برق‌پی"
                    refreshLabel="بروزرسانی از برق‌پی"
                    loading={barqDetailLoading}
                    onRefresh={() => handleRefreshBarqDetail({ silent: false })}
                  />
                  <BarqPortalStagesTimeline
                    stages={barqStages}
                    portalMessages={barqPortalFields.portalMessages}
                    showEmpty
                    emptyHint={
                      barqDetailLoading
                        ? "در حال دریافت مراحل از پنل برق‌پی…"
                        : "مراحل پنل برق‌پی هنوز دریافت نشده. اگر لاگین برق‌پی لازم است، از دکمه بروزرسانی استفاده کنید."
                    }
                  />
                </>
              ) : null}

              <Alert severity="info" sx={{ py: 0.75 }}>
                {isBarqCase
                  ? "بالا مراحل واقعی پنل برق‌پی دیده می‌شود؛ پایین مراحل داخلی سولارنت است که اپراتور ثبت می‌کند. هر دو فهرست می‌توانند متفاوت باشند."
                  : "از این صفحه می‌توانید وضعیت هر مرحله وام را ثبت کنید، اقدامات گردش کار را تکمیل کنید و مدارک/قرارداد را بارگذاری و تأیید نمایید — بدون نیاز به ورود به پرونده کامل."}
              </Alert>

              <LoanMehrsanProcessStepsTable
                domain="loan"
                title={
                  isBarqCase
                    ? "مراحل ثبت‌شده در سولارنت"
                    : `مراحل فرآیند ${partnerTitle}`
                }
                steps={loanSteps}
                loading={loading}
                canEditSteps
                onEditStep={(step) => setEditState({ open: true, step })}
              />

              <WorkflowActionsCard
                source={OPERATIONS_SOURCE}
                loanProcessId={loanProcessId}
                canEdit
              />

              <WorkflowDocumentsCard
                source={OPERATIONS_SOURCE}
                loanProcessId={loanProcessId}
                canEdit
                fileBaseUrl={fileBaseUrl}
              />
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <LoanMehrsanStepEditDialog
        open={editState.open}
        onClose={() => setEditState({ open: false, step: null })}
        step={editState.step}
        domain="loan"
        fileBaseUrl={fileBaseUrl}
        saving={saving}
        onSave={handleSaveStep}
      />

      <BarqPortalOtpLoginModal
        open={barqLoginOpen}
        onClose={() => setBarqLoginOpen(false)}
        onSuccess={async () => {
          toast.success("ورود برق‌پی ذخیره شد");
          await handleRefreshBarqDetail({ silent: false });
        }}
        source={OPERATIONS_SOURCE}
      />
    </>
  );
};

LendingWorkQueueCaseDrawer.propTypes = {
  open: PropTypes.bool,
  queueRow: PropTypes.object,
  partnerTitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

export default LendingWorkQueueCaseDrawer;
